import React, { useState, useEffect } from 'react';
import { X, User, Calendar, Clock, MapPin, Camera, FileText, CheckSquare, HelpCircle, Check, AlertTriangle, Loader2, ZoomIn, Download } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { reportsEarningsService } from '../../../services/reportsEarningsService';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import { cn } from '../../../utils/cn';

// Format date - supports user preference
const formatDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const weekday = d.toLocaleDateString('en-AU', { weekday: 'short' });
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
    const year = d.getFullYear();

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${weekday}, ${monthShort} ${day}, ${year}`;
      case 'YYYY-MM-DD':
        return `${weekday}, ${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${weekday}, ${day} ${monthShort} ${year}`;
    }
  } catch {
    return dateString;
  }
};

// Format time - supports user preference
const formatTime = (timeString, timeFormat = '12h') => {
  if (!timeString) return '—';
  try {
    const d = new Date(timeString);
    if (timeFormat === '24h') {
      return d.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return timeString;
  }
};

// Format hours
const formatHours = (hours) => {
  if (!hours) return '0h';
  const h = parseFloat(hours);
  return `${h.toFixed(1)} hours`;
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return `$${parseFloat(amount).toFixed(2)}`;
};

// Status badge configuration - paid status removed since payment should only be handled in Earnings page
const STATUS_CONFIG = {
  submitted: { label: 'Pending Review', className: 'bg-orange-500/20 text-orange-400' },
  approved: { label: 'Approved', className: 'bg-green-500/20 text-green-400' },
  disputed: { label: 'Disputed', className: 'bg-red-500/20 text-red-400' },
  needs_revision: { label: 'Needs Revision', className: 'bg-yellow-500/20 text-yellow-400' }
};

/**
 * ReportDetailModal
 *
 * Full-screen modal showing complete details of a submitted job report.
 * Includes photos, work notes, checklist, questionnaire, and timesheet info.
 */
export default function ReportDetailModal({
  instance,
  onClose,
  onApprove,
  onReject,
  onDispute
}) {
  const { preferences } = useUserPreferences();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (instance?.id) {
      loadReportDetails();
    }
  }, [instance?.id]);

  const loadReportDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await reportsEarningsService.getReportDetails(instance.id);
      setReportData(data);
    } catch (err) {
      console.error('Error loading report details:', err);
      setError(err?.message || 'Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const status = reportData?.status || instance?.status;
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
  // Timesheet can be an array or a single object depending on query
  const reportTimesheet = Array.isArray(reportData?.timesheet) ? reportData?.timesheet?.[0] : reportData?.timesheet;
  const instanceTimesheet = Array.isArray(instance?.timesheet) ? instance?.timesheet?.[0] : instance?.timesheet;
  const timesheet = reportTimesheet || instanceTimesheet;
  const contractor = reportData?.contractor || instance?.contractor;
  const job = reportData?.job || instance?.job;
  const photos = reportData?.photos || [];
  // Overtime request can also be array or object
  const reportOvertimeRequest = Array.isArray(reportData?.overtime_request) ? reportData?.overtime_request?.[0] : reportData?.overtime_request;
  const instanceOvertimeRequest = Array.isArray(instance?.overtime_request) ? instance?.overtime_request?.[0] : instance?.overtime_request;
  const overtimeRequest = reportOvertimeRequest || instanceOvertimeRequest;

  // Debug logging for timesheet issues
  console.log('ReportDetailModal - reportData?.timesheet:', reportData?.timesheet);
  console.log('ReportDetailModal - instance?.timesheet:', instance?.timesheet);
  console.log('ReportDetailModal - resolved timesheet:', timesheet);

  // Separate before, after, and issue photos
  const beforePhotos = photos?.filter(p => p.photo_type === 'before') || [];
  const afterPhotos = photos?.filter(p => p.photo_type === 'after') || [];
  const issuePhotos = photos?.filter(p => p.photo_type === 'notes') || [];

  // Download all photos in a section as a zip file (uses full-size images, not thumbnails)
  const handleDownloadAll = async (photosToDownload, folderName) => {
    if (photosToDownload.length === 0) return;

    setDownloading(folderName);
    try {
      const zip = new JSZip();
      const folder = zip.folder(folderName);

      await Promise.all(photosToDownload.map(async (photo, idx) => {
        // Use url (full size) for downloads, fallback to signedUrl for backwards compatibility
        const downloadUrl = photo.url || photo.signedUrl;
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const extension = blob.type.split('/')[1] || 'jpg';
        folder.file(`photo-${idx + 1}.${extension}`, blob);
      }));

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${folderName}.zip`);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(null);
    }
  };

  // Parse checklist and questionnaire data
  const checklistData = reportData?.checklist_data || instance?.checklist_data || [];
  const questionnaireResponses = reportData?.questionnaire_responses || instance?.questionnaire_responses || [];

  return (
    <div className="fixed inset-0 z-[101] flex items-center justify-center bg-black/70 pt-16">
      <div className="bg-background w-full max-w-4xl max-h-[90vh] rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-text-primary">{job?.job_name || 'Job Report'}</h2>
            <p className="text-text-secondary text-sm">{job?.job_site?.address || job?.location_address}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={cn('px-3 py-1 rounded-full text-sm font-medium', statusConfig.className)}>
              {statusConfig.label}
            </span>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-6">
              {/* Contractor & Timesheet Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contractor Card */}
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <User size={16} />
                    Contractor
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-border rounded-full flex items-center justify-center">
                      {contractor?.avatar_url ? (
                        <img
                          src={contractor.avatar_url}
                          alt={contractor.full_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <User size={24} className="text-text-secondary" />
                      )}
                    </div>
                    <div>
                      <p className="text-text-primary font-medium">{contractor?.full_name}</p>
                      <p className="text-text-secondary text-sm">{contractor?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Timesheet Card */}
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <Clock size={16} />
                    Timesheet
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-text-secondary text-xs">Clock In</p>
                      <p className="text-text-primary">{formatTime(timesheet?.clock_in_time, preferences.time_format)}</p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">Clock Out</p>
                      <p className="text-text-primary">{formatTime(timesheet?.clock_out_time, preferences.time_format)}</p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">Total Hours</p>
                      <p className="text-text-primary font-medium">{formatHours(timesheet?.total_hours)}</p>
                    </div>
                    {timesheet?.estimated_hours && (
                      <div>
                        <p className="text-text-secondary text-xs">Estimated Hours</p>
                        <p className="text-text-primary">{formatHours(timesheet?.estimated_hours)}</p>
                      </div>
                    )}
                  </div>

                  {/* Payment Breakdown */}
                  <div className="mt-4 pt-4 border-t border-border-light">
                    <div className="space-y-2">
                      {/* Show base amount if overtime exists */}
                      {timesheet?.overtime_amount > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-text-secondary">
                              Base Pay ({formatHours(timesheet?.capped_hours || timesheet?.estimated_hours)})
                            </span>
                            <span className="text-text-primary">{formatCurrency(timesheet?.base_amount)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-orange-400">Overtime</span>
                            <span className="text-orange-400">{formatCurrency(timesheet?.overtime_amount)}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-border-light">
                            <span className="text-text-secondary font-medium">Total</span>
                            <span className="text-green-400 font-bold">{formatCurrency(timesheet?.amount_earned)}</span>
                          </div>
                        </>
                      )}
                      {/* Show simple amount if no overtime */}
                      {(!timesheet?.overtime_amount || timesheet?.overtime_amount <= 0) && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Amount</span>
                          <span className="text-green-400 font-medium">{formatCurrency(timesheet?.amount_earned)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduled Date */}
              <div className="bg-surface border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Calendar size={16} />
                  <span className="text-sm">Scheduled Date:</span>
                  <span className="text-text-primary">{formatDate(reportData?.scheduled_date || instance?.scheduled_date, preferences.date_format)}</span>
                </div>
              </div>

              {/* Overtime Request (if exists) */}
              {overtimeRequest && (
                <div className={cn(
                  "bg-surface border rounded-lg p-4",
                  overtimeRequest.status === 'pending' ? "border-orange-500/50 bg-orange-500/5" :
                  overtimeRequest.status === 'approved' ? "border-green-500/50 bg-green-500/5" :
                  overtimeRequest.status === 'partially_approved' ? "border-yellow-500/50 bg-yellow-500/5" :
                  overtimeRequest.status === 'rejected' ? "border-red-500/50 bg-red-500/5" :
                  "border-border"
                )}>
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <Clock size={16} />
                    Overtime Request
                    <span className={cn(
                      "ml-2 px-2 py-0.5 rounded-full text-xs font-medium",
                      overtimeRequest.status === 'pending' ? "bg-orange-500/20 text-orange-400" :
                      overtimeRequest.status === 'approved' ? "bg-green-500/20 text-green-400" :
                      overtimeRequest.status === 'partially_approved' ? "bg-yellow-500/20 text-yellow-400" :
                      overtimeRequest.status === 'rejected' ? "bg-red-500/20 text-red-400" :
                      "bg-border text-text-secondary"
                    )}>
                      {overtimeRequest.status === 'partially_approved' ? 'Partial' :
                       overtimeRequest.status?.charAt(0).toUpperCase() + overtimeRequest.status?.slice(1)}
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-text-secondary text-xs">Overtime Hours</p>
                      <p className="text-text-primary">{formatHours(overtimeRequest.overtime_hours)}</p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">Requested Amount</p>
                      <p className="text-orange-400">{formatCurrency(overtimeRequest.requested_overtime_amount)}</p>
                    </div>
                    {(overtimeRequest.status === 'approved' || overtimeRequest.status === 'partially_approved') && (
                      <div>
                        <p className="text-text-secondary text-xs">Approved Amount</p>
                        <p className="text-green-400 font-medium">{formatCurrency(overtimeRequest.approved_overtime_amount)}</p>
                      </div>
                    )}
                    {overtimeRequest.status === 'rejected' && overtimeRequest.rejection_reason && (
                      <div className="col-span-2">
                        <p className="text-text-secondary text-xs">Rejection Reason</p>
                        <p className="text-red-400 text-sm">{overtimeRequest.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                  {overtimeRequest.justification && (
                    <div className="mt-3 pt-3 border-t border-border-light">
                      <p className="text-text-secondary text-xs mb-1">Contractor's Justification</p>
                      <p className="text-text-primary text-sm">{overtimeRequest.justification}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Before Photos */}
              {beforePhotos.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-text-secondary flex items-center gap-2">
                      <Camera size={16} />
                      Before Photos ({beforePhotos.length})
                    </h3>
                    <button
                      onClick={() => handleDownloadAll(beforePhotos, 'before-photos')}
                      disabled={downloading === 'before-photos'}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {downloading === 'before-photos' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      Download All
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[264px]">
                    <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                      {beforePhotos.map((photo, idx) => (
                        <div
                          key={photo.id || idx}
                          className="relative aspect-square bg-border rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => setSelectedPhoto(photo.url || photo.signedUrl)}
                        >
                          <img
                            src={photo.thumbnailUrl || photo.signedUrl}
                            alt={`Before ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* After Photos */}
              {afterPhotos.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-text-secondary flex items-center gap-2">
                      <Camera size={16} />
                      After Photos ({afterPhotos.length})
                    </h3>
                    <button
                      onClick={() => handleDownloadAll(afterPhotos, 'after-photos')}
                      disabled={downloading === 'after-photos'}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {downloading === 'after-photos' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      Download All
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[264px]">
                    <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                      {afterPhotos.map((photo, idx) => (
                        <div
                          key={photo.id || idx}
                          className="relative aspect-square bg-border rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => setSelectedPhoto(photo.url || photo.signedUrl)}
                        >
                          <img
                            src={photo.thumbnailUrl || photo.signedUrl}
                            alt={`After ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Issue Photos */}
              {issuePhotos.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-text-secondary flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Issue Photos ({issuePhotos.length})
                    </h3>
                    <button
                      onClick={() => handleDownloadAll(issuePhotos, 'issue-photos')}
                      disabled={downloading === 'issue-photos'}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {downloading === 'issue-photos' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      Download All
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[264px]">
                    <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                      {issuePhotos.map((photo, idx) => (
                        <div
                          key={photo.id || idx}
                          className="relative aspect-square bg-border rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => setSelectedPhoto(photo.url || photo.signedUrl)}
                        >
                          <img
                            src={photo.thumbnailUrl || photo.signedUrl}
                            alt={`Issue ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Work Notes */}
              {reportData?.work_notes && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <FileText size={16} />
                    Work Notes & Issues
                  </h3>
                  <p className="text-text-primary whitespace-pre-wrap">{reportData.work_notes}</p>
                </div>
              )}

              {/* Checklist */}
              {checklistData.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <CheckSquare size={16} />
                    Checklist
                  </h3>
                  <div className="space-y-2">
                    {checklistData.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className={cn(
                          'w-5 h-5 rounded flex items-center justify-center',
                          item.completed ? 'bg-green-500' : 'bg-border'
                        )}>
                          {item.completed && <Check size={14} className="text-white" />}
                        </div>
                        <span className={cn(
                          'text-sm',
                          item.completed ? 'text-text-primary' : 'text-text-secondary'
                        )}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Questionnaire Responses */}
              {questionnaireResponses.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                    <HelpCircle size={16} />
                    Questionnaire Responses
                  </h3>
                  <div className="space-y-4">
                    {questionnaireResponses.map((item, idx) => (
                      <div key={idx}>
                        <p className="text-text-secondary text-sm mb-1">{item.question}</p>
                        <p className="text-text-primary">{item.answer || '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          {status === 'submitted' && (
            <>
              <button
                onClick={onDispute}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                <AlertTriangle size={18} />
                Dispute
              </button>
              <button
                onClick={onReject}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <X size={18} />
                Reject
              </button>
              <button
                onClick={onApprove}
                className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-600 text-white rounded-lg transition-colors"
              >
                <Check size={18} />
                Approve
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-60 bg-black/90 flex items-center justify-center"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg"
          >
            <X size={24} />
          </button>
          <img
            src={selectedPhoto}
            alt="Photo"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        </div>
      )}
    </div>
  );
}
